import { useQuery } from '@tanstack/react-query';
import { formatDatumIdo, formatFt } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { ELVESZETT_OK_CIMKE, HIVAS_EREDMENY_CIMKE, MEGBIZAS_TIPUS_CIMKE, STATUSZ_CIMKE, type LeadStatusz } from '../lib/tipusok.ts';
import { ARELVARAS_CIMKE, type Arelvaras } from '../../../web/src/lib/cimkek.ts';
import { Betolto, HibaUzenet, Kartya } from '../ui/elemek.tsx';
import { honapCimke } from './ujrahivas.ts';

interface Esemeny {
  id: number; tipus: string; adat: unknown; created_at: string; szerzo: { nev: string } | null;
}

function mezo(adat: unknown, kulcs: string): unknown {
  return adat && typeof adat === 'object' && !Array.isArray(adat) ? (adat as Record<string, unknown>)[kulcs] : undefined;
}

function statuszNev(v: unknown): string {
  return typeof v === 'string' ? STATUSZ_CIMKE[v as LeadStatusz] ?? v : '–';
}

/** A státuszhoz rögzített részlet (megbízás típusa, tényleges ár, szemle időpontja, újrahívás hónapja, elvesztés oka); a régi eseményekben nincs. */
function statuszReszlet(adat: unknown): string | undefined {
  const tipus = mezo(adat, 'megbizas_tipus');
  if (typeof tipus === 'string') return `Megbízás típusa: ${MEGBIZAS_TIPUS_CIMKE[tipus] ?? tipus}`;
  const ar = mezo(adat, 'tenyleges_ar');
  if (typeof ar === 'number') return `Tényleges eladási ár: ${formatFt(ar)}`;
  const szemle = mezo(adat, 'szemle_idopont');
  if (typeof szemle === 'string') return `Szemle időpontja: ${formatDatumIdo(szemle)}`;
  const honap = mezo(adat, 'ujrahivas_honap');
  if (typeof honap === 'string') return `Újrahívás: ${honapCimke(honap)}`;
  const ok = mezo(adat, 'elveszett_ok');
  if (typeof ok === 'string') return `Ok: ${ELVESZETT_OK_CIMKE[ok] ?? ok}`;
  return undefined;
}

export function esemenySzoveg(e: Esemeny, nevek: Map<string, string>): { cim: string; torzs?: string } {
  switch (e.tipus) {
    case 'statusz':
      return { cim: `${statuszNev(mezo(e.adat, 'uj'))} (korábban: ${statuszNev(mezo(e.adat, 'regi'))})`, torzs: statuszReszlet(e.adat) };
    case 'statusz_adat':
      return { cim: `${statuszNev(mezo(e.adat, 'statusz'))}: módosított adat`, torzs: statuszReszlet(e.adat) };
    case 'jegyzet': {
      const arelvaras = mezo(e.adat, 'arelvaras');
      if (typeof arelvaras === 'string') {
        return { cim: 'Árelvárás', torzs: ARELVARAS_CIMKE[arelvaras as Arelvaras] ?? arelvaras };
      }
      const t = mezo(e.adat, 'szoveg');
      return { cim: 'Jegyzet', torzs: typeof t === 'string' ? t : '' };
    }
    case 'hivas': {
      const er = mezo(e.adat, 'eredmeny');
      return { cim: `Hívás: ${typeof er === 'string' ? HIVAS_EREDMENY_CIMKE[er] ?? er : '–'}` };
    }
    case 'kiosztas': {
      const id = mezo(e.adat, 'user_id');
      if (mezo(e.adat, 'tartalek') === true) return { cim: 'Tartalékként a vezetőhöz került' };
      return { cim: `Kiosztva: ${typeof id === 'string' ? nevek.get(id) ?? 'ismeretlen kolléga' : 'ismeretlen kolléga'}` };
    }
    case 'atosztas': {
      // Az `ertesites` hamis, ha az átosztó magának osztotta (az adatbázis-trigger így rögzíti).
      if (mezo(e.adat, 'ertesites') === false) return { cim: 'Magának osztotta ki' };
      const id = mezo(e.adat, 'user_id');
      return { cim: `Átosztva: ${typeof id === 'string' ? nevek.get(id) ?? 'ismeretlen kolléga' : 'ismeretlen kolléga'}` };
    }
    case 'kiosztas_sikertelen':
      return { cim: 'A kiosztás nem sikerült, nincs aktív kolléga' };
    case 'uzenet':
      return { cim: 'Üzenet', torzs: typeof mezo(e.adat, 'szoveg') === 'string' ? String(mezo(e.adat, 'szoveg')) : undefined };
    case 'bejovo_level': {
      const targy = mezo(e.adat, 'targy');
      const t = mezo(e.adat, 'szoveg');
      return { cim: `Válaszolt e-mailben${typeof targy === 'string' && targy ? `: ${targy}` : ''}`, torzs: typeof t === 'string' ? t : undefined };
    }
    default:
      return { cim: e.tipus };
  }
}

export type EsemenySzin = 'kiemelt' | 'masodlagos' | 'email' | 'jegyzet' | 'rendszer' | 'hiba';

/** Az esemény típusa az idővonalon: színes sáv + ugyanolyan színű felirat (a szín önmagában nem elég, ezért a név is). */
export function esemenyKategoria(e: Esemeny): { nev: string; szin: EsemenySzin } {
  switch (e.tipus) {
    case 'statusz':
    case 'statusz_adat': return { nev: 'Státusz', szin: 'kiemelt' };
    case 'hivas': return { nev: 'Hívás', szin: 'masodlagos' };
    case 'bejovo_level': return { nev: 'E-mail', szin: 'email' };
    case 'uzenet': return { nev: 'Üzenet', szin: 'email' };
    case 'jegyzet': return { nev: typeof mezo(e.adat, 'arelvaras') === 'string' ? 'Árelvárás' : 'Jegyzet', szin: 'jegyzet' };
    case 'kiosztas':
    case 'atosztas': return { nev: 'Kiosztás', szin: 'rendszer' };
    case 'kiosztas_sikertelen': return { nev: 'Kiosztás', szin: 'hiba' };
    default: return { nev: 'Esemény', szin: 'rendszer' };
  }
}

// Teljes osztálynevek (a Tailwind csak a forrásban szó szerint szereplőket generálja).
const SAV: Record<EsemenySzin, string> = {
  kiemelt: 'border-kiemelt', masodlagos: 'border-masodlagos', email: 'border-esemeny-email',
  jegyzet: 'border-esemeny-jegyzet', rendszer: 'border-vonal-eros', hiba: 'border-hiba',
};
const FELIRAT: Record<EsemenySzin, string> = {
  kiemelt: 'text-kiemelt', masodlagos: 'text-masodlagos', email: 'text-esemeny-email',
  jegyzet: 'text-esemeny-jegyzet', rendszer: 'text-szoveg-halk', hiba: 'text-hiba',
};

export default function Idovonal({ leadId }: { leadId: string }) {
  const lista = useQuery({
    queryKey: ['esemenyek', leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_esemenyek')
        .select('id, tipus, adat, created_at, szerzo:profiles(nev)')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as Esemeny[];
    },
  });
  // A kiosztási és átosztási eseményekben csak user_id van; a nevekhez külön, a lead eseményeihez szükséges profilokat kérjük le.
  const idk = [...new Set((lista.data ?? []).flatMap((e) => {
    const id = e.tipus === 'kiosztas' || e.tipus === 'atosztas' ? mezo(e.adat, 'user_id') : undefined;
    return typeof id === 'string' ? [id] : [];
  }))];
  const nevek = useQuery({
    queryKey: ['esemeny-nevek', leadId, idk.join(',')],
    enabled: idk.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('user_id, nev').in('user_id', idk);
      if (error) throw error;
      return new Map(data.map((p) => [p.user_id, p.nev] as const));
    },
  });

  return (
    <Kartya>
      <h2 className="mb-3 text-[17px] font-medium tracking-[-0.015em]">Idővonal</h2>
      {lista.isPending && <Betolto />}
      <HibaUzenet hiba={lista.error ?? nevek.error} />
      {lista.data && lista.data.length === 0 && <p className="text-szoveg-halk">Még nincs esemény.</p>}
      <ol className="grid gap-3">
        {lista.data?.map((e) => {
          const s = esemenySzoveg(e, nevek.data ?? new Map());
          const k = esemenyKategoria(e);
          return (
            <li key={e.id} className={`grid gap-0.5 border-l-[3px] pl-3.5 ${SAV[k.szin]}`}>
              <span className={`text-xs font-semibold ${FELIRAT[k.szin]}`}>{k.nev}</span>
              <span className="font-medium">{s.cim}</span>
              {s.torzs && <p className="whitespace-pre-wrap wrap-anywhere">{s.torzs}</p>}
              <span className="flex flex-wrap gap-x-3 text-sm text-szoveg-halk">
                <span>{e.szerzo?.nev ?? 'Rendszer'}</span>
                <span className="szam">{formatDatumIdo(e.created_at)}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </Kartya>
  );
}
